import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Truck, RefreshCw, MapPin, AlertTriangle, CheckCircle2, Clock,
  Plus, Trash2, Save, Settings as SettingsIcon, Phone, KeyRound, Package, Loader2, ArrowRight, X, Recycle, Locate, Lock,
} from "lucide-react";

const TABS = [
  { id: "today", label: "Today" },
  { id: "boys", label: "Delivery Boys" },
  { id: "empties", label: "Empty Tiffins" },
  { id: "settings", label: "Settings" },
];

export default function AdminDelivery() {
  const [tab, setTab] = useState("today");
  return (
    <div data-testid="admin-delivery-page">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 rounded-xl bg-primary/10 text-primary items-center justify-center"><Truck className="h-5 w-5" /></span>
        <div>
          <p className="text-xs tracking-overline uppercase font-bold text-secondary">Operations</p>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">Tiffin delivery</h1>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        Auto-generated daily roster of tiffins to deliver, grouped by pincode. Hand off to delivery boys with exact tiffin counts, verify each delivery via OTP, and reconcile at the end of the trip — losses are flagged automatically.
      </p>

      <div className="mt-7 inline-flex bg-muted/50 rounded-full p-1" role="tablist" data-testid="delivery-tabs">
        {TABS.map((t) => (
          <button
            key={t.id} type="button" onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${tab === t.id ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="mt-7">
        {tab === "today" && <TodayPanel />}
        {tab === "boys" && <BoysPanel />}
        {tab === "empties" && <EmptiesPanel />}
        {tab === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

// =================== TODAY ===================
function TodayPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date] = useState(""); // future: date picker
  const [activeHandoff, setActiveHandoff] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/delivery/today${date ? `?date=${date}` : ""}`);
      setData(r.data);
    } catch (e) { toast.error("Could not load roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading roster…</div>;
  if (!data) return null;

  const totalToday = (data.lunch?.total || 0) + (data.dinner?.total || 0);
  const totalFull = (data.lunch?.full || 0) + (data.dinner?.full || 0);
  const totalHalf = (data.lunch?.half || 0) + (data.dinner?.half || 0);
  const outsideTotal = (data.lunch?.outside_count || 0) + (data.dinner?.outside_count || 0);

  return (
    <div className="space-y-6" data-testid="today-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Roster for</p>
          <p className="font-display font-extrabold text-2xl mt-1">{new Date(data.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total tiffins" value={totalToday} icon={Package} />
        <Stat label="Full tiffins" value={totalFull} icon={Package} accent />
        <Stat label="Half tiffins" value={totalHalf} icon={Package} accent />
        <Stat label="Outside service" value={outsideTotal} icon={AlertTriangle} warn={outsideTotal > 0} />
      </div>

      {/* iter-131.14: dispatch-ready counters. Big, readable, kitchen-tablet friendly. */}
      <div className="grid grid-cols-2 gap-3" data-testid="dispatch-summary">
        <DispatchTile label="Lunch to dispatch" stats={data.ready_stats?.lunch} meal="lunch" />
        <DispatchTile label="Dinner to dispatch" stats={data.ready_stats?.dinner} meal="dinner" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <MealCard meal="lunch" bucket={{ ...data.lunch, date: data.date }} cutoff={data.settings?.lunch_cutoff} onHandoff={setActiveHandoff} readyStats={data.ready_stats?.lunch} onReadyChanged={load} />
        <MealCard meal="dinner" bucket={{ ...data.dinner, date: data.date }} cutoff={data.settings?.dinner_cutoff} onHandoff={setActiveHandoff} readyStats={data.ready_stats?.dinner} onReadyChanged={load} />
      </div>

      {data.handoffs?.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Today's handoffs</p>
          {data.handoffs.map((h) => <HandoffCard key={h.handoff_id} handoff={h} onChange={load} />)}
        </div>
      )}

      {activeHandoff && (
        <HandoffSheet
          ctx={activeHandoff}
          onClose={() => setActiveHandoff(null)}
          onCreated={async () => { setActiveHandoff(null); await load(); }}
        />
      )}
    </div>
  );
}

function DispatchTile({ label, stats, meal }) {
  const s = stats || { total: 0, ready: 0, pending: 0 };
  const pct = s.total ? Math.round((s.ready * 100) / s.total) : 0;
  const status = s.total === 0 ? "empty" : s.pending === 0 ? "green" : s.ready > 0 ? "amber" : "red";
  const palette = {
    empty: { bg: "bg-muted", ring: "ring-border",           bar: "bg-muted-foreground", label: "text-muted-foreground" },
    red:   { bg: "bg-destructive/10", ring: "ring-destructive/30",  bar: "bg-destructive", label: "text-destructive" },
    amber: { bg: "bg-amber-50",       ring: "ring-amber-300/60",    bar: "bg-amber-500",   label: "text-amber-800" },
    green: { bg: "bg-emerald-50",     ring: "ring-emerald-300/60",  bar: "bg-emerald-600", label: "text-emerald-800" },
  }[status];
  return (
    <div className={`rounded-2xl p-4 ring-1 ${palette.bg} ${palette.ring}`} data-testid={`dispatch-tile-${meal}`}>
      <p className={`text-[10px] tracking-overline uppercase font-bold ${palette.label}`}>{label}</p>
      <p className="font-display font-extrabold text-3xl mt-1 tabular-nums">{s.total}</p>
      <div className="mt-2 h-1.5 rounded-full bg-black/5 overflow-hidden">
        <div className={`h-full transition-all ${palette.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`text-xs mt-1.5 font-semibold ${palette.label}`}>
        {status === "empty" && "No tiffins scheduled"}
        {status === "red"   && `${s.pending} pending — kitchen not started`}
        {status === "amber" && `${s.ready}/${s.total} ready · waiting on ${s.pending}`}
        {status === "green" && `All ${s.total} ready — dispatch unlocked`}
      </p>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, warn }) {
  return (    <div className={`rounded-2xl p-4 ${warn ? "bg-destructive/10 border border-destructive/30" : accent ? "bg-primary/5 border border-primary/15" : "bg-card border border-border"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-overline uppercase font-bold text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${warn ? "text-destructive" : "text-muted-foreground"}`} />
      </div>
      <p className={`font-display font-extrabold text-3xl mt-2 ${warn ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}

function MealCard({ meal, bucket, cutoff, onHandoff, readyStats, onReadyChanged }) {
  const Title = meal === "lunch" ? "Lunch" : "Dinner";
  const stats = readyStats || { total: bucket.total, ready: 0, pending: bucket.total };
  const pct = stats.total ? Math.round((stats.ready * 100) / stats.total) : 0;
  const allReady = stats.total > 0 && stats.pending === 0;

  const bulkReady = async () => {
    try {
      await api.post("/admin/delivery/roster/batch-ready", { date: bucket.date || undefined, meal_type: meal });
      toast.success(`All ${meal} tiffins marked ready`);
      onReadyChanged && onReadyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark batch ready");
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`meal-card-${meal}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] tracking-overline uppercase font-bold text-secondary">{meal === "lunch" ? "Mid-day" : "Evening"}</p>
          <h3 className="font-display font-extrabold text-2xl">{Title} · {bucket.total} tiffin{bucket.total !== 1 ? "s" : ""}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {bucket.full} full · {bucket.half} half
            {cutoff && <> · cut-off <Clock className="inline h-3 w-3 ml-1 -mt-0.5" /> {cutoff}</>}
          </p>
        </div>
        {/* iter-131.14: kitchen-ready progress + bulk toggle. When 100 %
            of items are `ready`, the delivery boy's dispatch button turns
            green (else amber / red). */}
        {bucket.total > 0 && (
          <div className="shrink-0 flex items-center gap-3">
            <div className="text-right leading-tight">
              <p className={`text-[10px] tracking-overline uppercase font-bold ${allReady ? "text-emerald-700" : "text-amber-700"}`}>Kitchen ready</p>
              <p className="text-sm font-mono font-extrabold tabular-nums" data-testid={`ready-count-${meal}`}>{stats.ready}/{stats.total} ({pct}%)</p>
            </div>
            {!allReady && (
              <Button
                size="sm"
                onClick={bulkReady}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid={`bulk-ready-${meal}`}
              >
                Mark all ready
              </Button>
            )}
          </div>
        )}
      </div>

      {bucket.groups.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4">No deliveries scheduled.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {bucket.groups.map((g) => (
            <details key={g.pincode} className="rounded-xl border border-border bg-background open:shadow-sm" data-testid={`pincode-group-${meal}-${g.pincode}`}>
              <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className={`h-4 w-4 shrink-0 ${g.is_outside ? "text-destructive" : "text-primary"}`} />
                  <span className="font-semibold truncate">
                    {g.pincode === "unknown" ? "Pincode missing" : g.pincode}
                  </span>
                  {g.is_outside && <span className="text-[10px] tracking-overline uppercase font-bold bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">Outside</span>}
                  {g.pincode === "unknown" && <span className="text-[10px] tracking-overline uppercase font-bold bg-secondary/15 text-secondary px-2 py-0.5 rounded-full">Update address</span>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold tabular-nums">{g.count}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{g.full}F · {g.half}H</span>
                </div>
              </summary>
              <ul className="border-t border-border divide-y divide-border">
                {g.items.map((it) => <ItemRow key={it.roster_id} item={it} onReadyChanged={onReadyChanged} />)}
                <li className="px-4 py-3 bg-muted/30">
                  <Button
                    size="sm"
                    onClick={() => onHandoff({ meal, pincode: g.pincode, items: g.items, full: g.full, half: g.half })}
                    className="rounded-full bg-primary hover:bg-primary/90"
                    data-testid={`handoff-${meal}-${g.pincode}`}
                  >
                    <Truck className="h-3.5 w-3.5 mr-2" /> Hand over to delivery boy
                  </Button>
                </li>
              </ul>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, onReadyChanged }) {
  const statusBadge = {
    planned: "bg-muted text-muted-foreground",
    ready:   "bg-emerald-100 text-emerald-700",
    out: "bg-secondary/15 text-secondary",
    delivered: "bg-emerald-100 text-emerald-700",
    undelivered: "bg-amber-100 text-amber-700",
    returned: "bg-amber-100 text-amber-700",
  }[item.status] || "bg-muted text-muted-foreground";
  const isReady = item.status === "ready" || item.status === "out" || item.status === "delivered";
  const toggleReady = async () => {
    try {
      await api.post(`/admin/delivery/roster/${item.roster_id}/ready`, { ready: !isReady });
      onReadyChanged && onReadyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle");
    }
  };
  const prefs = item.tiffin_preferences || null;
  const prefsSummary = prefs && typeof prefs === "object"
    ? Object.entries(prefs).map(([k, v]) => `${k}: ${typeof v === "boolean" ? (v ? "yes" : "no") : v}`).join(" · ")
    : (typeof prefs === "string" ? prefs : "");
  return (
    <li className="px-4 py-3 flex items-start gap-3 text-sm" data-testid={`item-${item.roster_id}`}>
      <span className={`text-[10px] tracking-overline uppercase font-bold px-2 py-0.5 rounded-full ${item.tiffin_size === "full" ? "bg-primary/10 text-primary" : "bg-secondary/15 text-secondary"}`}>
        {item.tiffin_size}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold truncate">{item.name}</p>
        <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Phone className="h-3 w-3" /> {item.phone}</p>
        <p className="text-xs text-muted-foreground line-clamp-2">{item.address}</p>
        {prefsSummary && (
          <p className="mt-1 text-[10.5px] font-semibold text-primary/80 line-clamp-2" data-testid={`item-prefs-${item.roster_id}`}>
            <span className="uppercase tracking-wider text-[9px] mr-1 opacity-70">Prefs:</span>
            {prefsSummary}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className={`text-[10px] tracking-overline uppercase font-bold px-2 py-0.5 rounded-full ${statusBadge}`}>{item.status}</span>
        {item.status === "planned" || item.status === "ready" ? (
          <button
            type="button"
            onClick={toggleReady}
            data-testid={`ready-toggle-${item.roster_id}`}
            className={`text-[10px] font-extrabold px-2 py-1 rounded-full uppercase tracking-wide ${isReady ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
          >
            {isReady ? "Ready ✓" : "Mark ready"}
          </button>
        ) : null}
      </div>
    </li>
  );
}

// Handoff modal
function HandoffSheet({ ctx, onClose, onCreated }) {
  const [boys, setBoys] = useState([]);
  const [boyId, setBoyId] = useState("");
  const [takenFull, setTakenFull] = useState(ctx.full);
  const [takenHalf, setTakenHalf] = useState(ctx.half);
  const [selected, setSelected] = useState(new Set(ctx.items.filter((i) => !i.handoff_id).map((i) => i.roster_id)));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => {
    try { const r = await api.get("/admin/delivery/boys"); setBoys((r.data.boys || []).filter((b) => b.active !== false)); } catch {}
  })(); }, []);

  const items = ctx.items;
  const expectedFull = items.filter((i) => selected.has(i.roster_id) && i.tiffin_size === "full").length;
  const expectedHalf = items.filter((i) => selected.has(i.roster_id) && i.tiffin_size === "half").length;
  const extra = Math.max(0, takenFull - expectedFull) + Math.max(0, takenHalf - expectedHalf);

  const submit = async () => {
    if (!boyId) { toast.error("Pick a delivery boy"); return; }
    if (selected.size === 0) { toast.error("Pick at least one tiffin"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/delivery/handoff", {
        meal_type: ctx.meal,
        delivery_boy_id: boyId,
        roster_ids: Array.from(selected),
        tiffins_taken_full: Number(takenFull),
        tiffins_taken_half: Number(takenHalf),
      });
      toast.success(`Handoff created · ${r.data.tiffins_taken_total} tiffins out`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={onClose} data-testid="handoff-sheet">
      <div className="bg-card w-full md:max-w-lg md:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-start justify-between sticky top-0 bg-card z-10">
          <div>
            <p className="text-xs tracking-overline uppercase font-bold text-secondary">Hand over · {ctx.meal}</p>
            <h3 className="font-display font-extrabold text-xl mt-1">Pincode {ctx.pincode}</h3>
            <p className="text-xs text-muted-foreground mt-1">{selected.size} tiffins selected</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="handoff-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Delivery boy</label>
            <select value={boyId} onChange={(e) => setBoyId(e.target.value)} className="mt-2 w-full h-11 rounded-xl border border-input bg-background px-3 text-sm" data-testid="handoff-boy">
              <option value="">— select —</option>
              {boys.map((b) => <option key={b.boy_id} value={b.boy_id}>{b.name} · {b.phone}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Items in this handoff</label>
            <ul className="mt-2 space-y-2 max-h-64 overflow-y-auto border border-border rounded-xl">
              {items.map((it) => {
                const isSel = selected.has(it.roster_id);
                const taken = !!it.handoff_id;
                return (
                  <li key={it.roster_id} className={`flex items-center gap-3 px-3 py-2 ${taken ? "opacity-50" : ""}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={isSel} disabled={taken}
                      onChange={() => {
                        const n = new Set(selected);
                        n.has(it.roster_id) ? n.delete(it.roster_id) : n.add(it.roster_id);
                        setSelected(n);
                      }}
                    />
                    <span className={`text-[10px] tracking-overline uppercase font-bold px-1.5 py-0.5 rounded-full ${it.tiffin_size === "full" ? "bg-primary/10 text-primary" : "bg-secondary/15 text-secondary"}`}>{it.tiffin_size?.[0] || "?"}</span>
                    <span className="text-sm flex-1 truncate">{it.name} · {it.phone}</span>
                    {taken && <span className="text-[10px] uppercase font-bold text-muted-foreground">already taken</span>}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Boy taking (full)</label>
              <Input type="number" value={takenFull} onChange={(e) => setTakenFull(Number(e.target.value || 0))} min={0} className="mt-2 h-11" data-testid="handoff-taken-full" />
              <p className="text-[10px] text-muted-foreground mt-1">Expected: {expectedFull}</p>
            </div>
            <div>
              <label className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Boy taking (half)</label>
              <Input type="number" value={takenHalf} onChange={(e) => setTakenHalf(Number(e.target.value || 0))} min={0} className="mt-2 h-11" data-testid="handoff-taken-half" />
              <p className="text-[10px] text-muted-foreground mt-1">Expected: {expectedHalf}</p>
            </div>
          </div>

          {extra > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="handoff-extra-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">{extra} extra tiffin{extra !== 1 ? "s" : ""} compared to roster</p>
                <p className="opacity-80 mt-0.5">Recorded in the handoff. If they aren't returned at end-of-trip, marked as loss.</p>
              </div>
            </div>
          )}

          <Button onClick={submit} disabled={submitting} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold" data-testid="handoff-confirm">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : <>Hand over {Number(takenFull) + Number(takenHalf)} tiffins <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Existing handoff card (with mark-delivered + reconcile)
function HandoffCard({ handoff, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    if (expanded) (async () => {
      try { const r = await api.get(`/admin/delivery/handoff/${handoff.handoff_id}`); setItems(r.data.items || []); } catch {}
    })();
  }, [expanded, handoff.handoff_id]);

  const mark = async (rosterId, status) => {
    try {
      let extra = {};
      if (status === "delivered") {
        if (!navigator.geolocation) {
          toast.error("This device can't share GPS — open delivery on a phone with location enabled.");
          return;
        }
        toast.message("Getting your location…");
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
          );
          extra = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch {
          toast.error("Allow location access in your browser to mark deliveries.");
          return;
        }
      }
      await api.post(`/admin/delivery/roster/${rosterId}/mark`, { status, ...extra });
      toast.success(status === "delivered" ? "Geo-verified · delivered" : status);
      const r = await api.get(`/admin/delivery/handoff/${handoff.handoff_id}`);
      setItems(r.data.items || []);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      await api.post(`/admin/delivery/handoff/${handoff.handoff_id}/reconcile`, {});
      toast.success("Reconciled");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setReconciling(false); }
  };

  const isDone = handoff.status === "reconciled";

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`handoff-card-${handoff.handoff_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-overline uppercase font-bold text-secondary">{handoff.meal_type} · handoff</p>
          <h4 className="font-display font-bold text-lg mt-1">{handoff.delivery_boy_name}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Took {handoff.tiffins_taken_total} ({handoff.tiffins_taken_full}F + {handoff.tiffins_taken_half}H) · expected {handoff.expected_total}
          </p>
        </div>
        <div className="text-right">
          {isDone ? (
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {handoff.loss_count > 0
                ? <span className="text-xs font-bold bg-destructive/10 text-destructive px-2 py-1 rounded-full" data-testid="loss-badge">⚠ {handoff.loss_count} LOSS</span>
                : <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full"><CheckCircle2 className="inline h-3 w-3" /> All accounted</span>}
              <span className="text-[10px] text-muted-foreground">{handoff.delivered_count} delivered · {handoff.returned_count} returned</span>
            </div>
          ) : (
            <span className="text-xs font-bold bg-secondary/15 text-secondary px-2 py-1 rounded-full">OUT</span>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => setExpanded((e) => !e)} variant="outline" size="sm" className="rounded-full" data-testid={`expand-${handoff.handoff_id}`}>
          {expanded ? "Hide items" : "Show items"}
        </Button>
        {!isDone && (
          <Button onClick={reconcile} disabled={reconciling} size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid={`reconcile-${handoff.handoff_id}`}>
            {reconciling ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1.5" />} End trip & reconcile
          </Button>
        )}
      </div>

      {expanded && (
        <ul className="mt-4 border-t border-border divide-y divide-border">
          {items.map((it) => (
            <li key={it.roster_id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`handoff-item-${it.roster_id}`}>
              <span className={`text-[10px] tracking-overline uppercase font-bold px-2 py-0.5 rounded-full ${it.tiffin_size === "full" ? "bg-primary/10 text-primary" : "bg-secondary/15 text-secondary"}`}>{it.tiffin_size}</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{it.name}</p>
                <p className="text-xs text-muted-foreground truncate">{it.phone} · {it.address}</p>
              </div>
              {it.status === "delivered" ? (
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <span
                    className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                      it.confirmed_by === "customer"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                    data-testid={`confirmed-by-${it.roster_id}`}
                  >
                    {it.confirmed_by === "customer" ? "Customer confirmed" : "Geo-verified"}
                  </span>
                  {it.distance_m !== undefined && (
                    <span className="text-[10px] text-muted-foreground" title="Distance from customer">
                      {Math.round(it.distance_m)}m
                    </span>
                  )}
                </div>
              ) : it.status === "returned" || it.status === "undelivered" ? (
                <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{it.status}</span>
              ) : !isDone ? (
                <div className="flex items-center gap-1.5">
                  <Button size="sm" onClick={() => mark(it.roster_id, "delivered")} className="h-8 rounded-full bg-primary hover:bg-primary/90" data-testid={`mark-delivered-${it.roster_id}`}>
                    <KeyRound className="h-3 w-3 mr-1" /> Mark delivered
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => mark(it.roster_id, "returned")} className="h-8 rounded-full" data-testid={`mark-returned-${it.roster_id}`}>Return</Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =================== BOYS ===================
function BoysPanel() {
  const [boys, setBoys] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pins, setPins] = useState("");

  const load = async () => { try { const r = await api.get("/admin/delivery/boys"); setBoys(r.data.boys || []); } catch {} };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("Name & phone required"); return; }
    try {
      await api.post("/admin/delivery/boys", {
        name: name.trim(), phone: phone.trim(),
        assigned_pincodes: pins.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setName(""); setPhone(""); setPins("");
      toast.success("Delivery boy added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (b, patch) => {
    try { await api.patch(`/admin/delivery/boys/${b.boy_id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name}?`)) return;
    try { await api.delete(`/admin/delivery/boys/${b.boy_id}`); load(); toast.success("Deleted"); } catch {}
  };

  return (
    <div className="space-y-6" data-testid="boys-panel">
      <div className="bg-card rounded-2xl border border-border p-5">
        <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground">Add delivery boy</p>
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="boy-name" />
          <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" data-testid="boy-phone" />
          <Input placeholder="Assigned pincodes (comma-separated)" value={pins} onChange={(e) => setPins(e.target.value)} className="h-11" data-testid="boy-pins" />
        </div>
        <Button onClick={create} className="mt-3 rounded-full bg-primary hover:bg-primary/90" data-testid="add-boy"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>

      <div className="space-y-2">
        {boys.length === 0 && <p className="text-sm text-muted-foreground">No delivery boys yet.</p>}
        {boys.map((b) => (
          <div key={b.boy_id} className="bg-card rounded-2xl border border-border p-4 flex flex-wrap items-center gap-3" data-testid={`boy-${b.boy_id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{b.name} <span className="text-xs text-muted-foreground font-normal">· {b.phone}</span></p>
              <p className="text-xs text-muted-foreground mt-0.5">Pincodes: {b.assigned_pincodes?.join(", ") || "—"}</p>
            </div>
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={b.active !== false} onChange={(e) => update(b, { active: e.target.checked })} className="h-4 w-4 accent-primary" /> Active</label>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-full text-destructive" onClick={() => remove(b)} data-testid={`del-boy-${b.boy_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =================== EMPTIES ===================
function EmptiesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/delivery/empties"); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load empties"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const collect = async (u, count = 1) => {
    setCollecting(u.user_id);
    try {
      await api.post("/admin/delivery/empty/collect", { user_id: u.user_id, count });
      toast.success(`Collected ${count} from ${u.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCollecting(null); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading empty-tiffin ledger…</div>;
  if (!data) return null;
  const list = data.users || [];

  return (
    <div className="space-y-5" data-testid="empties-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Customers holding" value={data.count} icon={Recycle} accent />
        <Stat label="Tiffins outstanding" value={data.total_outstanding} icon={Package} accent />
        <Stat label="Estimated loss" value={`₹${(data.total_outstanding * 120).toLocaleString("en-IN")}`} icon={AlertTriangle} warn={data.total_outstanding > 0} />
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center text-sm text-muted-foreground">
          All tiffins accounted for. No empties pending.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((u) => (
            <div key={u.user_id} className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 flex flex-wrap items-center gap-3" data-testid={`empties-row-${u.user_id}`}>
              <span className="inline-flex h-9 min-w-9 px-2 rounded-full bg-amber-600 text-white items-center justify-center text-sm font-bold">×{u.tiffin_balance}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.phone} · pin {u.pincode || "?"} · {u.address || "no address"}</p>
              </div>
              <Button onClick={() => collect(u, 1)} disabled={collecting === u.user_id} size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid={`admin-collect-${u.user_id}`}>
                {collecting === u.user_id ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Recycle className="h-3 w-3 mr-1.5" />}
                Collect 1
              </Button>
              {u.tiffin_balance > 1 && (
                <Button onClick={() => collect(u, u.tiffin_balance)} disabled={collecting === u.user_id} size="sm" variant="outline" className="rounded-full" data-testid={`admin-collect-all-${u.user_id}`}>
                  Collect all {u.tiffin_balance}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================== SETTINGS ===================
function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);

  useEffect(() => { (async () => {
    try {
      const [sr, hr] = await Promise.all([
        api.get("/admin/delivery/settings"),
        api.get("/admin/delivery/health"),
      ]);
      setS(sr.data);
      setHealth(hr.data);
    } catch {}
  })(); }, []);

  if (!s) return <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading settings…</div>;

  const upd = (patch) => setS({ ...s, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/delivery/settings", {
        ...s,
        service_pincodes: typeof s.service_pincodes === "string"
          ? s.service_pincodes.split(",").map((p) => p.trim()).filter(Boolean)
          : s.service_pincodes,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const applySuggested = () => {
    if (health?.suggested_geofence_m) upd({ geofence_meters: health.suggested_geofence_m });
  };

  const pinDispatch = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    toast.message("Reading kitchen GPS…");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        upd({ dispatch_lat: p.coords.latitude, dispatch_lng: p.coords.longitude });
        toast.success(`Pinned · ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}. Hit Save to lock it in.`);
      },
      () => toast.error("Allow location access on this device to pin dispatch"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearDispatch = () => upd({ dispatch_lat: null, dispatch_lng: null });

  return (
    <div className="space-y-6" data-testid="settings-panel">
      {health?.show_hint && (
        <div className="rounded-2xl bg-amber-50 border border-amber-300 p-4 md:p-5 flex flex-wrap items-start gap-3" data-testid="geofence-hint">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900">Geofence may be too tight</p>
            <p className="text-xs text-amber-900/80 mt-1">
              In the last 7 days, <b>{health.rejected_too_far}</b> of <b>{health.total_attempts}</b> delivery attempts were rejected as "too far"
              {" "}({Math.round(health.rejection_rate * 100)}%). Your delivery boys may be at the door but their phone GPS reads off.
              {health.suggested_geofence_m && (
                <> Try raising the radius to <b>{health.suggested_geofence_m}m</b> — that would have admitted ~95% of those.</>
              )}
            </p>
            {health.suggested_geofence_m && (
              <Button onClick={applySuggested} size="sm" className="mt-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="apply-suggested-geofence">
                Use {health.suggested_geofence_m}m
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="reminders-section">
        <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground flex items-center gap-1.5">
          <Recycle className="h-3.5 w-3.5" /> Empty-tiffin SMS reminders
        </p>
        <p className="text-xs text-muted-foreground">
          When on, customers holding ≥1 empty tiffin get an SMS this many minutes before each delivery slot opens. Set MSG91 env vars (MSG91_AUTH_KEY / MSG91_SENDER_ID / MSG91_FLOW_TIFFIN) to enable real sending — otherwise calls are logged as 'stub'.
        </p>
        <div className="grid md:grid-cols-2 gap-4 items-end">
          <Field label="Reminder enabled">
            <label className="inline-flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={s.reminder_enabled !== false}
                onChange={(e) => upd({ reminder_enabled: e.target.checked })}
                className="h-4 w-4 accent-primary"
                data-testid="reminder-enabled"
              />
              <span className="text-sm">Send SMS reminders</span>
            </label>
          </Field>
          <Field label="Lead time (minutes before slot opens)">
            <Input
              type="number"
              min={5}
              max={120}
              value={s.reminder_lead_minutes ?? 30}
              onChange={(e) => upd({ reminder_lead_minutes: Number(e.target.value || 30) })}
              data-testid="reminder-lead"
            />
          </Field>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="dispatch-section">
        <div>
          <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Dispatch (kitchen) location
          </p>
          <p className="text-sm text-muted-foreground mt-1">Anchors live tracking maps to a {s.dispatch_radius_km || 15} km service zone. Set this from the device sitting at the kitchen.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Latitude">
            <Input type="number" step="0.000001" value={s.dispatch_lat ?? ""} onChange={(e) => upd({ dispatch_lat: e.target.value === "" ? null : Number(e.target.value) })} data-testid="dispatch-lat" />
          </Field>
          <Field label="Longitude">
            <Input type="number" step="0.000001" value={s.dispatch_lng ?? ""} onChange={(e) => upd({ dispatch_lng: e.target.value === "" ? null : Number(e.target.value) })} data-testid="dispatch-lng" />
          </Field>
          <Field label="Service radius (km)">
            <Input type="number" min={1} max={50} value={s.dispatch_radius_km ?? 15} onChange={(e) => upd({ dispatch_radius_km: Number(e.target.value || 15) })} data-testid="dispatch-radius" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={pinDispatch} variant="outline" size="sm" className="rounded-full" data-testid="pin-dispatch">
            <Locate className="h-3.5 w-3.5 mr-1.5" /> Use this device's GPS
          </Button>
          <Button onClick={clearDispatch} variant="outline" size="sm" className="rounded-full text-destructive" data-testid="clear-dispatch">
            Clear
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="slot-windows">
        <p className="text-xs tracking-overline uppercase font-bold text-muted-foreground flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" /> Dispatch slot windows (IST)
        </p>
        <p className="text-xs text-muted-foreground">Boy can only start a slot's run during these times — prevents accidental wrong-slot dispatches.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lunch opens"><Input value={s.lunch_dispatch_open || "08:00"} onChange={(e) => upd({ lunch_dispatch_open: e.target.value })} placeholder="HH:MM" data-testid="lunch-open" /></Field>
            <Field label="Lunch closes"><Input value={s.lunch_dispatch_close || "14:00"} onChange={(e) => upd({ lunch_dispatch_close: e.target.value })} placeholder="HH:MM" data-testid="lunch-close" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dinner opens"><Input value={s.dinner_dispatch_open || "15:00"} onChange={(e) => upd({ dinner_dispatch_open: e.target.value })} placeholder="HH:MM" data-testid="dinner-open" /></Field>
            <Field label="Dinner closes"><Input value={s.dinner_dispatch_close || "22:00"} onChange={(e) => upd({ dinner_dispatch_close: e.target.value })} placeholder="HH:MM" data-testid="dinner-close" /></Field>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Lunch cut-off"><Input value={s.lunch_cutoff || ""} onChange={(e) => upd({ lunch_cutoff: e.target.value })} placeholder="HH:MM" data-testid="lunch-cutoff" /></Field>
          <Field label="Dinner cut-off"><Input value={s.dinner_cutoff || ""} onChange={(e) => upd({ dinner_cutoff: e.target.value })} placeholder="HH:MM" data-testid="dinner-cutoff" /></Field>
        </div>
        <Field label="Service pincodes (comma-separated · empty = all areas allowed)">
          <Textarea
            rows={3}
            value={Array.isArray(s.service_pincodes) ? s.service_pincodes.join(", ") : (s.service_pincodes || "")}
            onChange={(e) => upd({ service_pincodes: e.target.value })}
            data-testid="service-pincodes"
          />
        </Field>
        <Field label="Geofence radius (metres) — delivery rejected if boy is farther than this">
          <Input
            type="number"
            value={s.geofence_meters ?? 250}
            onChange={(e) => upd({ geofence_meters: Number(e.target.value || 0) })}
            min={5} max={2000}
            data-testid="geofence-meters"
          />
        </Field>
        <Button onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-settings">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-overline uppercase font-bold text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
